// src/pages/contas-a-pagar/components/ManualPaymentModal.jsx
import React, { useEffect, useMemo, useState } from "react";
import Button from "../../../components/ui/Button";
import Input from "../../../components/ui/Input";
import Select from "../../../components/ui/Select";
import Icon from "../../../components/AppIcon";

const ManualPaymentModal = ({
  isOpen,
  onClose,
  payable,
  onConfirm,
  isProcessing = false,
  errorMessage = "",
  formatCurrency,
  resolveSupplierName,
}) => {
  const initialAmount = useMemo(() => String(Number(payable?.amount || 0)), [payable]);

  const [form, setForm] = useState({
    paymentMethod: "",
    paymentDate: "",
    amount: "",
    bankAccount: "",
    reference: "",
    notes: "",
  });

  useEffect(() => {
    if (!isOpen) return;

    const today = new Date();
    const yyyy = String(today.getFullYear());
    const mm = String(today.getMonth() + 1).padStart(2, "0");
    const dd = String(today.getDate()).padStart(2, "0");

    setForm({
      paymentMethod: "",
      paymentDate: `${yyyy}-${mm}-${dd}`,
      amount: initialAmount,
      bankAccount: "",
      reference: "",
      notes: "",
    });
  }, [isOpen, initialAmount]);

  const update = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));

  const paymentMethodOptions = [
    { value: "DINHEIRO", label: "Dinheiro" },
    { value: "PIX", label: "Pix" },
    { value: "BOLETO", label: "Boleto" },
    { value: "CARTAO_CREDITO", label: "Cartão de crédito" },
    { value: "CARTAO_DEBITO", label: "Cartão de débito" },
    { value: "TRANSFERENCIA", label: "Transferência" },
    { value: "OUTROS", label: "Outros" },
  ];

  const canSubmit =
    !!form.paymentMethod &&
    !!form.paymentDate &&
    !!form.bankAccount &&
    Number(form.amount || 0) > 0;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit || isProcessing) return;

    await onConfirm?.({
      paymentMethod: form.paymentMethod,
      paymentDate: form.paymentDate, // YYYY-MM-DD
      amount: Number(form.amount),
      bankAccount: form.bankAccount,
      reference: form.reference?.trim(),
      notes: form.notes?.trim(),
    });
  };

  if (!isOpen) return null;

  const supplierName = resolveSupplierName ? resolveSupplierName(payable) : payable?.supplier || "Fornecedor";
  const displayAmount = formatCurrency
    ? formatCurrency(payable?.amount)
    : new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(Number(payable?.amount || 0));

  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-xl rounded-lg border border-border bg-card shadow-xl">
        <div className="flex items-center justify-between border-b border-border p-4">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-full bg-warning/10 flex items-center justify-center">
              <Icon name="CreditCard" size={18} />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Efetivar pagamento</p>
              <h3 className="font-heading-semibold text-lg text-card-foreground">{supplierName}</h3>
            </div>
          </div>

          <button
            type="button"
            className="rounded-md p-2 hover:bg-accent/40"
            onClick={onClose}
            aria-label="Fechar"
            disabled={isProcessing}
          >
            <Icon name="X" size={18} />
          </button>
        </div>

        <div className="p-4">
          <div className="rounded-md border border-border bg-accent/20 p-3 mb-4">
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Valor do título</span>
              <span className="font-heading-semibold text-card-foreground">{displayAmount}</span>
            </div>
            <div className="flex items-center justify-between text-xs mt-1">
              <span className="text-muted-foreground">Vencimento</span>
              <span className="text-card-foreground">{payable?.dueDateBR || "-"}</span>
            </div>
          </div>

          {errorMessage ? (
            <div className="mb-4 rounded-md border border-error/30 bg-error/10 p-3 text-sm text-error">
              {errorMessage}
            </div>
          ) : null}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-body-medium text-muted-foreground mb-1">
                  Forma de pagamento
                </label>
                <Select
                  value={form.paymentMethod}
                  onChange={(v) => update("paymentMethod", v)}
                  options={paymentMethodOptions}
                  placeholder="Selecione..."
                />
              </div>

              <div>
                <label className="block text-xs font-body-medium text-muted-foreground mb-1">
                  Data do pagamento
                </label>
                <Input
                  type="date"
                  value={form.paymentDate}
                  onChange={(e) => update("paymentDate", e.target.value)}
                />
              </div>

              <div>
                <label className="block text-xs font-body-medium text-muted-foreground mb-1">
                  Valor pago
                </label>
                <Input
                  type="number"
                  step="0.01"
                  value={form.amount}
                  onChange={(e) => update("amount", e.target.value)}
                  placeholder="0,00"
                />
              </div>

              <div>
                <label className="block text-xs font-body-medium text-muted-foreground mb-1">
                  Conta / Banco (texto livre)
                </label>
                <Input
                  value={form.bankAccount}
                  onChange={(e) => update("bankAccount", e.target.value)}
                  placeholder="Ex: Itaú - CC 1234-5"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-body-medium text-muted-foreground mb-1">
                Referência
              </label>
              <Input
                value={form.reference}
                onChange={(e) => update("reference", e.target.value)}
                placeholder="Ex: Comprovante, ID interno, etc."
              />
            </div>

            <div>
              <label className="block text-xs font-body-medium text-muted-foreground mb-1">
                Observações
              </label>
              <Input
                value={form.notes}
                onChange={(e) => update("notes", e.target.value)}
                placeholder="Opcional"
              />
            </div>

            <div className="flex items-center justify-end gap-2 pt-2">
              <Button variant="outline" type="button" onClick={onClose} disabled={isProcessing}>
                Cancelar
              </Button>

              <Button
                variant="primary"
                type="submit"
                iconName="Check"
                iconPosition="left"
                loading={isProcessing}
                disabled={!canSubmit}
              >
                {isProcessing ? "Processando..." : "Confirmar Pagamento"}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ManualPaymentModal;
